import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * AuthCallbackPage handles the OAuth callback from WorkOS AuthKit.
 * It extracts the authorization code from URL params and exchanges it for a JWT token.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleWorkOSCallback } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth errors from WorkOS
      if (errorParam) {
        setError(errorDescription || `Authentication error: ${errorParam}`);
        setProcessing(false);
        return;
      }

      // Ensure we have an authorization code
      if (!code) {
        setError('No authorization code received from authentication provider.');
        setProcessing(false);
        return;
      }

      try {
        // Exchange code for JWT token via backend
        const result = await handleWorkOSCallback(code, state);

        if (result.success) {
          // Check for invite code from OAuth state (passed through WorkOS)
          if (result.inviteCode) {
            // Redirect back to invite page to complete acceptance
            navigate(`/invite/${result.inviteCode}`, { replace: true });
          } else if (result.isNewUser) {
            // Redirect new users to onboarding
            navigate('/onboarding', { replace: true });
          } else {
            navigate('/feed', { replace: true });
          }
        } else {
          setError(result.error || 'Authentication failed. Please try again.');
          setProcessing(false);
        }
      } catch (err) {
        console.error('Callback processing error:', err);
        setError('An unexpected error occurred during authentication.');
        setProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, handleWorkOSCallback, navigate]);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Error</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button
            onClick={() => navigate('/auth', { replace: true })}
            variant="outline"
          >
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
